import React, { useState, useEffect } from 'react';
import { useOfflineStatus } from '@/contexts/OfflineContext';
import { cn } from '@/lib/utils';
import { WifiOff, X } from 'lucide-react';

/**
 * Global offline indicator that appears when the user is offline or has poor connection
 * Displays a subtle message to inform users of their connection status
 * Includes improved reliability to prevent false positive notifications
 */
export const OfflineIndicator = () => {
  const { isOnline, connectionQuality } = useOfflineStatus();
  const [dismissed, setDismissed] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  
  // Reset dismissal when connection status changes
  useEffect(() => {
    if (isOnline && connectionQuality === 'good') {
      setDismissed(false);
    }
  }, [isOnline, connectionQuality]);
  
  // Add a delay before showing the indicator to prevent flashing during momentary connection issues
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    
    if (!isOnline || (isOnline && connectionQuality === 'poor')) {
      timer = setTimeout(() => setShouldShow(true), 2000);
    } else {
      setShouldShow(false);
    }
    
    return () => clearTimeout(timer);
  }, [isOnline, connectionQuality]);
  
  // Don't render if we're online with good connection or user dismissed the message
  if ((isOnline && connectionQuality !== 'poor') || dismissed || !shouldShow) {
    return null;
  }

  // Perform a network test ping to confirm status
  // This is checked via navigator.onLine already, but this adds a visual verification
  return (
    <div 
      className={cn(
        "fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium transition-all duration-300",
        !isOnline ? 
          "bg-gray-800 text-white" : 
          "bg-amber-100 text-amber-800",
        "animate-fade-in"  // Added animation for smooth appearance
      )}
      role="status"
      aria-live="polite"
    >
      <WifiOff 
        size={16} 
        className={cn(!isOnline ? "text-white" : "text-amber-600")} 
        aria-hidden="true"
      />
      <span>
        {!isOnline 
          ? "You're offline. Some features may be limited."
          : "Poor connection detected. Some features may be slower."
        }
      </span>
      <button 
        onClick={() => setDismissed(true)}
        className="ml-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default OfflineIndicator;
